import * as Sentry from '@sentry/angular';
import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from './app/app.config';
import { App } from './app/app';
import { environment } from './environments/environment';

/**
 * Sentry bootstrap. Runs BEFORE the Angular app so failures during bootstrap are captured too.
 * OFF by default: an empty `sentryDsn` skips init entirely. Mirrors the user app and the backend
 * DSN gate — set the DSN only on deployed environments.
 */
if (environment.sentryDsn) {
  Sentry.init({
    dsn: environment.sentryDsn,
    environment: environment.production ? 'production' : 'development',
    release: environment.appVersion,
    tracesSampleRate: 0,
    // Crypto-tax data: never let the SDK auto-collect anything identifying. Only a bare user id is
    // attached elsewhere. Session Replay stays OFF — it would record client tax data.
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: { request: false, response: false },
      httpBodies: [],
      urlQueryParams: false,
    },
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      'AbortError',
    ],
    denyUrls: [/^chrome:\/\//i, /^chrome-extension:\/\//i, /^moz-extension:\/\//i, /^safari-extension:\/\//i],
    beforeSend: scrubSensitiveData,
  });
}

/** Last line of defence against leaking PII — drop request data, reduce user to a bare id. */
function scrubSensitiveData(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  if (event.request) {
    delete event.request.cookies;
    delete event.request.data;
    delete event.request.query_string;
    delete event.request.headers;
  }
  if (event.user) {
    event.user = event.user.id ? { id: String(event.user.id) } : {};
  }
  return event;
}

bootstrapApplication(App, appConfig).catch((err) => {
  // A bootstrap failure means the app never rendered — report it explicitly since no ErrorHandler
  // exists yet. captureException is a safe no-op when Sentry was not initialised.
  Sentry.captureException(err, { level: 'fatal', tags: { phase: 'bootstrap' } });
  console.error(err);
});
